<template>
  <div class="wrapper_dark" @click="interactCheck">
    <div class="header">
      <button class="btn_back" @click="back">
        <img src="@/assets/img/left-arrow.svg" />
      </button>
      <h1 class="title">{{ $tc("select_person") }}</h1>
      <button class="btn_exit" @click="goHome">{{ $tc("cancle") }}</button>
    </div>
    <div ref="innerWrap" class="inner_wrap" @scroll="onPersonnelScroll">
      <div class="danger_box">
        <div class="title">{{ $tc("order_number") }}</div>
        <div class="text" v-html="$tc('seats_available')"></div>
      </div>
      <!-- TB-14763: 인원 유형 사용 매장 - 매장에서 설정한 유형별로 인원 선택 (4개 이상이면 2단) -->
      <div
        v-if="usePersonnelTypes"
        class="personnel-types-wrap"
        :class="{ 'two-column': isTwoColumn }"
      >
        <div
          v-for="(type, idx) in personnelTypeList"
          :key="`${type.name}_${idx}`"
          class="item"
          :class="{ 'has-desc': getPersonnelTypeDescription(type) }"
        >
          <div class="title">
            <div class="name">{{ getMultiLanguageName(type, locale) }}</div>
            <div v-if="getPersonnelTypeDescription(type)" class="desc">({{ getPersonnelTypeDescription(type) }})</div>
          </div>
          <div class="sel_box">
            <button
              class="minus"
              :class="{ disabled: personnelCounts[idx] <= 0 }"
              @click="changePersonnelCount('decrease', idx)"
            ></button>
            <div class="count">{{ personnelCounts[idx] }}</div>
            <button
              class="plus"
              @click="changePersonnelCount('increase', idx)"
            ></button>
          </div>
        </div>
      </div>
      <!-- 항목이 많아 스크롤이 생기면 하단 고정 버튼 위에 그라디언트 + 더 보기 안내 -->
      <div v-if="usePersonnelTypes" v-show="isShowSeeMore" class="see-more">▼ {{ $tc('see_more') }}</div>
      <!-- TB-14951: 인원 유형 미사용 매장은 총인원 하나로 받는다 (isChildOption 에 따른 성인/유아 구분은 제거) -->
      <div v-else class="person-select-wrap">
        <div class="item">
          <div class="title">
            {{ $tc("total_number") }}
          </div>
          <div class="sel_box">
            <button
              class="minus"
              :class="{ disabled: adultPerson <= minAdultPerson }"
              @click="personCount('decrease')"
            ></button>
            <div class="count">{{ adultPerson }}</div>
            <button
              class="plus"
              @click="personCount('increase')"
            ></button>
          </div>
        </div>
      </div>
      <button class="next_btn" :class="{ active: isActive }" @click="next">
        {{ $tc("next") }}
      </button>
    </div>
    <Popup v-show="showPopup" @popControl="popup" />
    <AlertPopup v-if="isAlert" :msg="msg" @AlertEvent="alertAction" />
    <timeout-popup
      :isShow="isShowTimeOutPop"
      @moveToHome="moveToHome"
      @close="closeTimeoutPopup"
    />
  </div>
</template>

<script>
import { timeout } from './mixins/timeout'
import { viewport } from './mixins/viewport'
import timeoutPopup from '@/components/TimeOutPopup.vue'
import Popup from '@/components/Popup.vue'
import AlertPopup from '@/components/AlertPopup'
import { getCustomMaxPersonMsg, getMultiLanguageName, getMultiLanguageField, isUsePersonnelTypes, sumPersonnelCounts } from '../utills'
import { mapState, mapGetters } from 'vuex'
export default {
  name: 'SelectPerson',
  mixins: [timeout, viewport],
  components: {
    timeoutPopup,
    Popup,
    AlertPopup
  },
  data () {
    return {
      isShowTimeOutPop: false,
      timeoutObj: null,
      adultPerson: 1, // 인원 유형 미사용 매장의 총인원
      personnelCounts: [], // TB-14763: 인원 유형별 선택 인원 (restaurant.personnelTypes 와 같은 순서)
      isPersonnelScrollable: false, // 인원 유형이 많아 페이지 스크롤 발생 여부
      isPersonnelScrolledToBottom: true, // 페이지 스크롤 끝 도달 여부
      isActive: true,
      showPopup: false,
      msg: '',
      isAlert: false
    }
  },
  computed: {
    ...mapState(['locale']),
    ...mapGetters({
      restaurant: 'getRestaurantInfo',
      waitingParams: 'getWaitingParams'
    }),
    // TB-14763: 인원 유형 사용 매장 여부 (설정 on + 유형 1개 이상)
    usePersonnelTypes () {
      return isUsePersonnelTypes(this.restaurant)
    },
    personnelTypeList () {
      return this.usePersonnelTypes ? this.restaurant.personnelTypes : []
    },
    // 유형 3개 이하 1단, 4개 이상 2단 노출 (TB-14862)
    isTwoColumn () {
      return this.personnelTypeList.length >= 4
    },
    // 유형별 선택 인원을 서버 요청 형태로 변환 [{ ...type, count }]
    selectedPersonnelTypes () {
      return this.personnelTypeList.map((type, idx) => Object.assign({}, type, { count: this.personnelCounts[idx] || 0 }))
    },
    // 총인원 포함 유형 합계(numOfPeople) / 미포함 유형 합계(childCount)
    personnelSum () {
      return sumPersonnelCounts(this.selectedPersonnelTypes)
    },
    // 최대 인원 체크용 전체 선택 인원
    totalPersonCount () {
      return this.usePersonnelTypes
        ? this.personnelSum.numOfPeople + this.personnelSum.childCount
        : this.adultPerson
    },
    isShowSeeMore () {
      return this.isPersonnelScrollable && !this.isPersonnelScrolledToBottom
    },
    isMenuReuired () {
      return this.restaurant.menuRequired
    },
    maxPeople () {
      return this.restaurant.maxPeople
    },
    storeIdx () {
      return this.$store.getters.getStoreIdx
    },
    // 특정 매장은 최소 인원 2명부터 선택 가능 (TB-14470, 김삿갓밥집 사례)
    minAdultPerson () {
      return (process.env.VUE_APP_MIN_TWO_PERSON && process.env.VUE_APP_MIN_TWO_PERSON.includes(this.storeIdx)) ? 2 : 0
    }
  },
  watch: {
    adultPerson () {
      this.checkActive()
    },
    personnelCounts () {
      this.checkActive()
    }
  },
  methods: {
    getMultiLanguageName,
    // 인원 유형 추가 설명 (다국어, 없으면 빈 문자열)
    getPersonnelTypeDescription (type) {
      return getMultiLanguageField(type, 'description', this.locale)
    },
    checkActive () {
      // 총인원에 포함되는 인원(총인원 또는 isChild=false 유형 합계)이 1명 이상이어야 다음 진행 가능
      const mainCount = this.usePersonnelTypes ? this.personnelSum.numOfPeople : this.adultPerson
      if (this.restaurant.useLimitForWaiting) {
        this.isActive = mainCount > 0 && this.maxPeople >= this.totalPersonCount
      } else {
        this.isActive = mainCount > 0
      }
    },
    // 최대 인원 초과 여부 (maxPeople 0은 무제한)
    isOverMaxPeople () {
      return this.maxPeople !== 0 && this.maxPeople - 1 < this.totalPersonCount
    },
    // TB-14763: 인원 유형별 인원 증감
    changePersonnelCount (type, idx) {
      const current = this.personnelCounts[idx] || 0
      if (type === 'increase') {
        if (this.isOverMaxPeople()) {
          this.showMaxPeopleAlert()
          return
        }
        this.$set(this.personnelCounts, idx, current + 1)
      } else {
        this.$set(this.personnelCounts, idx, current > 0 ? current - 1 : 0)
      }
    },
    // 총인원 증감 (인원 유형 미사용 매장)
    personCount (type) {
      if (type === 'increase') {
        if (this.isOverMaxPeople()) {
          this.showMaxPeopleAlert()
          return
        }
        this.adultPerson++
      } else {
        this.adultPerson = this.adultPerson > this.minAdultPerson ? this.adultPerson - 1 : this.minAdultPerson
      }
    },
    // 최대 인원 초과 안내 팝업 노출 (매장별 커스텀 문구 포함)
    showMaxPeopleAlert () {
      this.isAlert = true
      // TB-14651: 다담석갈비(9427) - 최대인원수 초과접수시 안내문구 변경 필요
      const customMaxPersonMsg = getCustomMaxPersonMsg(this.storeIdx)
      if (customMaxPersonMsg) {
        this.msg = customMaxPersonMsg
        return
      }
      if (process.env.VUE_APP_APM_ENVIRONMENT === 'production') {
        // 운영일 경우만 동작
        // TB-1311: 최대인원수 초과하여 접수시 매장에 따라 안내문구 변경이 필요
        if (
          this.storeIdx &&
          ['8821', '3860'].includes(this.storeIdx.toString())
        ) {
          this.msg = `${this.maxPeople * 1 +
            1}명 이상은 2팀으로 나눠서 대기접수 해주세요.`
          // TB-4835: [FE][테이블링접수] 6466 홍원막국수 - 최대인원수 초과접수시 안내문구 변경 필요
        } else if (
          this.storeIdx &&
          ['6466'].includes(this.storeIdx.toString())
        ) {
          this.msg =
            '최대 대기등록인원은 8인입니다.<div style="margin-top:10px">(예) 12명일경우. 8명 4명<br/>또는 4명씩 3팀으로 나눠서<br/>예약하세요 (전화번호도 각각)</div>'
        } else if (
          this.storeIdx &&
          ['10118'].includes(this.storeIdx.toString())
        ) {
          // [TB-5126] [FE][테이블링접수] 10118 진주냉면 대연본점 - 최대인원수 초과접수시 안내문구 변경 필요
          this.msg =
            '6인 이상고객님들은 최대 4인씩 나눠서 등록 부탁드립니다. 그리고 인원이 모두 도착하신 후 등록부탁드립니다.<div style="margin-top:10px">ex) 7인일경우 4인/3인 나눠서 등록부탁드립니다.</div>'
        } else if (
          this.storeIdx &&
          ['4608'].includes(this.storeIdx.toString())
        ) {
          // [TB-9370] [FE][테이블링대기] 인원 수 제안 팝업 문구 수정 - 꼼다비뛰드(4608)
          this.msg = '주문하실 인원 수에 맞게 각자 웨이팅 부탁드립니다.'
        } else if (
          this.storeIdx &&
          [
            '12547',
            '12548',
            '12551',
            '12549',
            '12550',
            '12803',
            '12804'
          ].includes(this.storeIdx.toString())
        ) {
          // [TB-10455] [FE][접수/호출] 특정매장 대상 인원 선택 초과 시 팝업 텍스트 변경 - 유니클로
          this.msg = '1명만 신청 가능합니다.'
        } else if (
          this.storeIdx &&
          ['8900'].includes(this.storeIdx.toString())
        ) {
          // [TB-10734] [FE][테이블링접수] 8900 능동미나리 - 최대인원수 초과접수시 안내문구 변경 필요
          this.msg = `${this.maxPeople * 1 +
            1}명이상은 오른쪽 안내문 읽어주세요.`
        } else if (
          this.storeIdx &&
          ['6740'].includes(this.storeIdx.toString())
        ) {
          // [TB-10857] [FE][테이블링접수] 6740 신라제면 본점 - 최대인원수 초과접수시 안내문구 변경 필요
          // 6명 이상은 3명+3명/4명+2명 따로 대기등록 부탁드립니다. (대기 순번대로 한팀씩 입장합니다.)
          this.msg =
            '6명 이상은 3명+3명/4명+2명 따로 대기등록 부탁드립니다. (대기 순번대로 한팀씩 입장합니다.)'
        } else if (
          this.storeIdx &&
          ['4528'].includes(this.storeIdx.toString())
        ) {
          // [TB-12270] [FE][테이블링접수] 4528 델씨엘로 - 최대인원수 초과접수시 안내문구 변경 필요
          // 1. 모든 연령(영유아~100세)은 인원 수에 포함됩니다.
          // 2. 7인 부터는 2팀 이상으로 나누어 개별 입장이 가능합니다. (한 테이블에 의자 붙혀 앉기는 불가합니다.)
          this.msg =
            '1. 모든 연령(영유아~100세)은 인원 수에 포함됩니다.<br/>2. 7인 부터는 2팀 이상으로 나누어 개별 입장이 가능합니다.<br/>(한 테이블에 의자 붙혀 앉기는 불가합니다.)'
        } else {
          this.msg = this.$tc('ask_staff', this.maxPeople * 1 + 1)
        }
      } else {
        this.msg = this.$tc('ask_staff', this.maxPeople * 1 + 1)
      }
    },
    next () {
      if (!this.isActive) {
        return
      }
      // TB-14763: 인원 유형 사용 매장은 유형별 인원과 합계(numOfPeople/childCount)를 함께 저장
      const params = this.usePersonnelTypes
        ? Object.assign({}, this.personnelSum, { personnelTypes: this.selectedPersonnelTypes })
        : { numOfPeople: this.adultPerson, childCount: 0, personnelTypes: [] }
      // this.$store.commit('waitingParams', params)
      this.$store.commit('setWaitingParams', params)
      // if ((this.restaurant.isCWType && this.restaurant.isRemoteWaitingOrderType && this.restaurant.isWType) && (this.isMenuReuired && !this.waitingParams.isNoPhone) && this.waitingParams.email === '') {
      if (
        this.restaurant.isWType &&
        this.isMenuReuired &&
        !this.waitingParams.isNoPhone &&
        this.waitingParams.email === '' &&
        this.waitingParams.tempKey === ''
      ) {
        this.$router.push('/menu')
      } else {
        this.$router.push('/confirm')
      }
    },
    goHome () {
      this.showPopup = true
    },
    popup (type) {
      if (type === 'cancle') {
        this.$router.push('/')
      } else {
        this.showPopup = false
      }
    },
    back () {
      let goPath = this.restaurant.nameRequired ? '/privacy' : '/phone'
      if (
        (!this.restaurant.nameRequired && !this.restaurant.phoneRequired) ||
        (this.restaurant.isNumTicket && this.locale !== 'ko')
      ) {
        goPath = '/'
      }
      if (
        this.locale !== 'ko' &&
        this.restaurant.foreignRequestType &&
        this.restaurant.foreignRequestType.length > 0 &&
        this.waitingParams.email
      ) {
        goPath = '/email'
      }
      // LINE 접수는 개인정보 입력 화면이 없으므로 접수 방법 선택으로 돌아간다
      if (
        this.locale !== 'ko' &&
        this.restaurant.foreignRequestType &&
        this.restaurant.foreignRequestType.length > 0 &&
        this.waitingParams.tempKey
      ) {
        goPath = '/bifurcation'
      }
      this.$router.push(goPath)
    },
    alertAction (type) {
      if (type === 'close') {
        this.isAlert = false
      }
    },
    // TB-14763: 인원 유형별 인원 초기화 - 이전 화면에서 돌아온 경우 선택값 복원, 아니면 첫 번째 유형 1명
    initPersonnelCounts () {
      const saved = this.waitingParams.personnelTypes || []
      const isSameTypes = saved.length === this.personnelTypeList.length &&
        saved.every((type, idx) => type.name === this.personnelTypeList[idx].name)
      this.personnelCounts = this.personnelTypeList.map((type, idx) => {
        if (isSameTypes) return Number(saved[idx].count) || 0
        return idx === 0 ? 1 : 0
      })
    },
    // 페이지(inner_wrap) 스크롤 상태 갱신 (더 보기 안내 노출 판단)
    checkPersonnelScrollState () {
      const el = this.$refs.innerWrap
      if (!el) return
      this.isPersonnelScrollable = el.scrollHeight > el.clientHeight + 1
      this.isPersonnelScrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    },
    onPersonnelScroll () {
      this.checkPersonnelScrollState()
      this.interactCheck()
    }
  },
  mounted () {
    if (this.usePersonnelTypes) {
      this.initPersonnelCounts()
      this.$nextTick(this.checkPersonnelScrollState)
      return
    }
    this.adultPerson = Math.max(this.waitingParams.numOfPeople, this.minAdultPerson)
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/base.scss";

.inner_wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  // TB-14763: 인원 유형이 많으면 안내 문구를 포함한 페이지 전체가 스크롤 (헤더 70px, 하단 고정 버튼 94px 제외)
  height: calc(#{vh(100)} - 70px - 94px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  > * {
    flex-shrink: 0;
  }
}
.danger_box {
  display: flex;
  flex-direction: column;
  align-items: center;
  @include flex-gap(16px, column);
  padding: 36px 0;
  .title {
    display: flex;
    align-items: center;
    color: #d81b1b;
    text-align: center;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.125;
    &::before {
      content: "";
      display: block;
      width: 40px;
      height: 40px;
      margin-right: 8px;
      background: url("~@/assets/img/caution.svg") no-repeat center center;
    }
  }
  .text {
    color: #2e3137;
    text-align: center;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.5;
  }
}
.person-select-wrap {
  display: flex;
  flex-direction: column;
  @include flex-gap(32px, column);
  .item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 800px;
    padding: 20px 32px;
    border-radius: 20px;
    background: #eceef0;
    .title {
      display: flex;
      align-items: center;
      @include flex-gap(8px, row);
      color: #000000;
      font-size: 32px; // TB-14866: 총인원/성인/유아/커스텀 명칭 공통 32px / 36px
      font-weight: 700;
      line-height: 1.125;
      span {
        font-size: 28px;
        color: #6d7583;
      }
    }
    .sel_box {
      display: flex;
      align-items: center;
      @include flex-gap(24px, row);
      button {
        width: 70px;
        height: 70px;
        border-radius: 12px;
        border: 1px solid #b7bec8;
      }
      .minus {
        background: url("~@/assets/img/minus.svg") no-repeat $white;
        background-size: 30px 5px;
        background-position: center;
        &.disabled {
          background: url("../assets/img/minus-disabled.svg") no-repeat $white;
          background-position: center;
        }
      }
      .plus {
        background: url("~@/assets/img/plus.svg") no-repeat $white;
        background-size: 26px 26px;
        background-position: center;
      }
      .count {
        width: 70px;
        color: black;
        text-align: center;
        font-size: 40px;
        font-weight: 700;
        line-height: 1.35;
      }
    }
  }
}
// TB-14763: 인원 유형별 선택 영역
// 기준 해상도 1280x800: 1단 800px (TB-14880, 레거시 카드와 동일), 2단 600px x 2 + 간격 24px (TB-14865)
// 이 페이지의 세로 치수/글자 크기는 px 고정 - 뷰포트 높이가 부족하면 콘텐츠가 작아지는 대신 inner_wrap 이 스크롤된다
.personnel-types-wrap {
  width: 62.5vw; // 800px
  max-width: 800px;
  padding-bottom: 8px;
  // 박스 높이는 디자인 값(120px)을 최소로 두고 설명 줄 수에 따라 늘어난다 (TB-14864)
  // - 설명 없음: 유형명 최대 2줄 (말줄임)
  // - 설명 있음: 유형명 1줄 (말줄임) + 설명 전체 노출 (줄바꿈)
  .item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-height: 120px;
    padding: 20px 32px;
    border-radius: 20px;
    background: #eceef0;
    & + .item {
      margin-top: 24px;
    }
    .title {
      flex: 1;
      min-width: 0;
      margin-right: 24px;
      color: #000000;
      .name {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 32px; // TB-14866: 32px / 36px
        font-weight: 700;
        line-height: 1.125;
        word-break: keep-all;
      }
      .desc {
        margin-top: 4px;
        word-break: keep-all;
        overflow-wrap: break-word;
        color: #6d7583;
        font-size: 24px; // TB-14866: 24px / 38px
        font-weight: 500;
        line-height: 1.583;
      }
    }
    &.has-desc .title .name {
      display: block;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .sel_box {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      @include flex-gap(24px, row);
      button {
        width: 70px;
        height: 70px;
        border-radius: 12px;
        border: 1px solid #b7bec8;
      }
      .minus {
        background: url("~@/assets/img/minus.svg") no-repeat $white;
        background-size: 30px 5px;
        background-position: center;
        &.disabled {
          background: url("../assets/img/minus-disabled.svg") no-repeat $white;
          background-position: center;
        }
      }
      .plus {
        background: url("~@/assets/img/plus.svg") no-repeat $white;
        background-size: 26px 26px;
        background-position: center;
      }
      .count {
        width: 70px;
        color: black;
        text-align: center;
        font-size: 40px;
        font-weight: 700;
        line-height: 1.35;
      }
    }
  }
  // 유형 4개 이상: 2단 노출 (3개 이하는 1단, TB-14862)
  // 박스 1개 너비는 최대 600px (래퍼 1224px = 600 + 24 + 600)
  &.two-column {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch; // 같은 행의 카드는 높이를 맞춘다 (TB-14864)
    align-content: flex-start;
    width: 95.625vw; // 1224px
    max-width: 1224px;
    .item {
      width: 46.875vw; // 600px
      max-width: 600px;
      margin-top: 0;
      margin-bottom: 24px;
      &:nth-child(odd) {
        margin-right: 24px;
        margin-right: unquote("min(1.875vw, 24px)");
      }
    }
  }
}
// 스크롤 발생 시 하단 그라디언트 + 더 보기 안내 (고정 버튼 바로 위)
.see-more {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 94px; // .next_btn 높이
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 120px;
  padding-bottom: 14px;
  background: linear-gradient(180deg, rgba(248, 249, 250, 0) 0%, #f8f9fa 60%);
  color: #2e3137;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.727;
  pointer-events: none;
}
.next_btn {
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 94px;
  background: #b7bec8;
  color: rgba(255, 255, 255, 0.59);
  font-size: 36px;
  font-weight: 700;
  line-height: 1.055;
  &.active {
    cursor: pointer;
    color: #ffffff;
    background-color: #d81b1b;
  }
}
</style>
